
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Surgical Equipment Service
 * Uses backend API endpoints instead of direct Firestore access
 */
export const surgicalEquipmentService = {
  /**
   * Get all surgical equipment for a facility
   * @param {string} facilityId - Facility ID
   * @param {Object} options - Additional options (status, page, limit)
   * @returns {Promise<Array>} Promise resolving to array of equipment
   */
  getAllEquipment: async (facilityId, options = {}) => {
    try {
      console.log('Fetching surgical equipment for facility via API:', facilityId);
      const token = await getAuthToken();
      const { status, page = 1, limit = 50 } = options;
      
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/facility/${facilityId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Fetched surgical equipment:', response.data.equipment);
      return response.data.equipment;
    } catch (error) {
      console.error('Error fetching surgical equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch surgical equipment');
    }
  },

  /**
   * Get equipment by status for a facility
   * @param {string} facilityId - Facility ID
   * @param {string} status - Equipment status
   * @param {Object} options - Additional options (page, limit)
   * @returns {Promise<Array>} Promise resolving to array of equipment
   */
  getEquipmentByStatus: async (facilityId, status, options = {}) => {
    try {
      console.log('Fetching equipment by status via API:', status, 'for facility:', facilityId);
      
      return await surgicalEquipmentService.getAllEquipment(facilityId, { status, ...options });
    } catch (error) {
      console.error('Error fetching equipment by status:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch equipment by status');
    }
  },

  /**
   * Get a single equipment item by ID
   * @param {string} equipmentId - Equipment ID
   * @returns {Promise<Object|null>} Promise resolving to equipment data or null
   */
  getEquipmentById: async (equipmentId) => {
    try {
      console.log('Fetching equipment via API:', equipmentId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/${equipmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const equipment = response.data.equipment;
      console.log('Fetched equipment:', equipment);
      return equipment;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.log('Equipment not found:', equipmentId);
        return null;
      }
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to fetch equipment');
    }
  },

  /**
   * Create a new equipment item
   * @param {Object} equipmentData - The equipment data
   * @returns {Promise<string>} Promise resolving to the new equipment ID
   */
  createEquipment: async (equipmentData) => {
    try {
      console.log('Creating surgical equipment via API:', equipmentData);
      const token = await getAuthToken();
      
      // Validate required fields
      if (!equipmentData.facilityId) {
        throw new Error('Facility ID is required');
      }
      if (!equipmentData.name) {
        throw new Error('Equipment name is required');
      }

      const response = await axios.post(
        `${API_BASE}/surgical/surgical-equipment`,
        equipmentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Created surgical equipment with ID:', response.data.equipmentId);
      return response.data.equipmentId;
    } catch (error) {
      console.error('Error creating surgical equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to create surgical equipment');
    }
  },

  /**
   * Update an existing equipment item
   * @param {string} equipmentId - Equipment ID
   * @param {Object} equipmentData - Partial equipment data to update
   * @returns {Promise<void>}
   */
  updateEquipment: async (equipmentId, equipmentData) => {
    try {
      console.log('Updating surgical equipment via API:', equipmentId, equipmentData);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/surgical/surgical-equipment/${equipmentId}`,
        equipmentData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Updated surgical equipment:', equipmentId);
    } catch (error) {
      console.error('Error updating surgical equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to update surgical equipment');
    }
  },

  /**
   * Delete an equipment item
   * @param {string} equipmentId - Equipment ID
   * @returns {Promise<void>}
   */
  deleteEquipment: async (equipmentId) => {
    try {
      console.log('Deleting surgical equipment via API:', equipmentId);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/surgical/surgical-equipment/${equipmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Deleted surgical equipment:', equipmentId);
    } catch (error) {
      console.error('Error deleting surgical equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to delete surgical equipment');
    }
  },

  /**
   * Add maintenance record to equipment
   * @param {string} equipmentId - Equipment ID
   * @param {Object} maintenanceRecord - Maintenance record data
   * @returns {Promise<void>}
   */
  addMaintenanceRecord: async (equipmentId, maintenanceRecord) => {
    try {
      console.log('Adding maintenance record to equipment via API:', equipmentId, maintenanceRecord);
      const token = await getAuthToken();
      
      await axios.post(
        `${API_BASE}/surgical/surgical-equipment/${equipmentId}/maintenance`,
        maintenanceRecord,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Added maintenance record to equipment:', equipmentId);
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to add maintenance record');
    }
  },

  /**
   * Get equipment needing maintenance for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array>} Promise resolving to array of equipment needing maintenance
   */
  getMaintenanceDue: async (facilityId) => {
    try {
      console.log('Fetching equipment needing maintenance for facility via API:', facilityId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/facility/${facilityId}/maintenance-due`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Equipment needing maintenance:', response.data.maintenanceDue);
      return response.data.maintenanceDue;
    } catch (error) {
      // If maintenance-due endpoint doesn't exist, filter manually
      try {
        const allEquipment = await surgicalEquipmentService.getAllEquipment(facilityId);
        
        // Filter equipment where nextMaintenance is due
        const today = new Date().toISOString().split('T')[0];
        const maintenanceDue = allEquipment.filter(item => 
          item.nextMaintenance && item.nextMaintenance <= today
        );
        
        console.log('Equipment needing maintenance (manual filter):', maintenanceDue);
        return maintenanceDue;
      } catch (fallbackError) {
        console.error('Error fetching maintenance due equipment (fallback):', fallbackError);
        if (axios.isAxiosError(error) && error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw new Error(error.message || 'Failed to fetch maintenance due equipment');
      }
    }
  },

  // Additional helper methods for enhanced functionality

  /**
   * Get equipment with pagination and filtering
   * @param {string} facilityId - Facility ID
   * @param {Object} filters - Filtering options
   * @param {Object} pagination - Pagination options
   * @returns {Promise<Object>} Promise resolving to paginated results
   */
  getEquipmentPaginated: async (facilityId, filters = {}, pagination = {}) => {
    try {
      console.log('Fetching paginated equipment via API:', facilityId, filters);
      const token = await getAuthToken();
      const { page = 1, limit = 50 } = pagination;
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      // Add filter parameters
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.manufacturer) {
        params.append('manufacturer', filters.manufacturer);
      }
      if (filters.serialNumber) {
        params.append('serialNumber', filters.serialNumber);
      }
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/facility/${facilityId}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        equipment: response.data.equipment,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching paginated equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Search equipment by criteria
   * @param {string} facilityId - Facility ID
   * @param {string} searchTerm - Search term
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Array of matching equipment
   */
  searchEquipment: async (facilityId, searchTerm, filters = {}) => {
    try {
      console.log('Searching equipment via API:', facilityId, searchTerm);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/facility/${facilityId}/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.equipment;
    } catch (error) {
      // If search endpoint doesn't exist, fallback to getting all and filtering
      try {
        const allEquipment = await surgicalEquipmentService.getAllEquipment(facilityId);
        
        if (!searchTerm || searchTerm.trim() === '') {
          return allEquipment;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return allEquipment.filter(equipment => 
          (equipment.name && equipment.name.toLowerCase().includes(lowerSearchTerm)) ||
          (equipment.manufacturer && equipment.manufacturer.toLowerCase().includes(lowerSearchTerm)) ||
          (equipment.model && equipment.model.toLowerCase().includes(lowerSearchTerm)) ||
          (equipment.serialNumber && equipment.serialNumber.toLowerCase().includes(lowerSearchTerm)) ||
          (equipment.category && equipment.category.toLowerCase().includes(lowerSearchTerm))
        );
      } catch (fallbackError) {
        console.error('Error searching equipment (fallback):', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Get equipment statistics for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Equipment statistics
   */
  getEquipmentStats: async (facilityId) => {
    try {
      console.log('Getting equipment statistics via API:', facilityId);
      
      // Get all equipment for the facility and calculate stats
      const allEquipment = await surgicalEquipmentService.getAllEquipment(facilityId);
      
      return {
        totalEquipment: allEquipment.length,
        operational: allEquipment.filter(e => e.status === 'operational').length,
        maintenance: allEquipment.filter(e => e.status === 'maintenance').length,
        outOfService: allEquipment.filter(e => e.status === 'out-of-service').length,
        retired: allEquipment.filter(e => e.status === 'retired').length,
        maintenanceDue: allEquipment.filter(e => {
          const today = new Date().toISOString().split('T')[0];
          return e.nextMaintenance && e.nextMaintenance <= today;
        }).length,
        byCategory: allEquipment.reduce((acc, e) => {
          const category = e.type || 'Uncategorized';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {}),
        averageAge: null, // Would need additional calculation
        totalValue: allEquipment.reduce((sum, e) => sum + (e.value || 0), 0)
      };
    } catch (error) {
      console.error('Error getting equipment stats:', error);
      throw error;
    }
  },

  /**
   * Get available equipment for a procedure
   * @param {string} facilityId - Facility ID
   * @param {string} procedureType - Type of procedure
   * @returns {Promise<Array>} Array of available equipment
   */
  getAvailableEquipmentForProcedure: async (facilityId, procedureType) => {
    try {
      console.log('Getting available equipment for procedure via API:', facilityId, procedureType);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/facility/${facilityId}/available?procedureType=${procedureType}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.availableEquipment;
    } catch (error) {
      // If procedure-specific endpoint doesn't exist, get operational equipment
      try {
        return await surgicalEquipmentService.getEquipmentByStatus(facilityId, 'operational');
      } catch (fallbackError) {
        console.error('Error getting available equipment (fallback):', fallbackError);
        throw error;
      }
    }
  },

  /**
   * Reserve equipment for a procedure
   * @param {string} equipmentId - Equipment ID
   * @param {Object} reservationData - Reservation data
   * @returns {Promise<void>}
   */
  reserveEquipment: async (equipmentId, reservationData) => {
    try {
      console.log('Reserving equipment via API:', equipmentId, reservationData);
      const token = await getAuthToken();
      
      await axios.post(
        `${API_BASE}/surgical/surgical-equipment/${equipmentId}/reserve`,
        reservationData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Reserved equipment:', equipmentId);
    } catch (error) {
      console.error('Error reserving equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Release equipment reservation
   * @param {string} equipmentId - Equipment ID
   * @returns {Promise<void>}
   */
  releaseEquipmentReservation: async (equipmentId) => {
    try {
      console.log('Releasing equipment reservation via API:', equipmentId);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/surgical/surgical-equipment/${equipmentId}/reserve`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Released equipment reservation:', equipmentId);
    } catch (error) {
      console.error('Error releasing equipment reservation:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Update equipment status
   * @param {string} equipmentId - Equipment ID
   * @param {string} status - New status
   * @param {string} reason - Reason for status change
   * @returns {Promise<void>}
   */
  updateEquipmentStatus: async (equipmentId, status, reason = '') => {
    try {
      console.log('Updating equipment status via API:', equipmentId, status);
      
      await surgicalEquipmentService.updateEquipment(equipmentId, {
        status: status,
        statusReason: reason,
        statusUpdatedAt: new Date().toISOString()
      });
      
      console.log('Updated equipment status:', equipmentId, status);
    } catch (error) {
      console.error('Error updating equipment status:', error);
      throw error;
    }
  },

  /**
   * Schedule maintenance for equipment
   * @param {string} equipmentId - Equipment ID
   * @param {Object} maintenanceSchedule - Maintenance schedule data
   * @returns {Promise<void>}
   */
  scheduleMaintenanceForEquipment: async (equipmentId, maintenanceSchedule) => {
    try {
      console.log('Scheduling maintenance for equipment via API:', equipmentId, maintenanceSchedule);
      
      await surgicalEquipmentService.updateEquipment(equipmentId, {
        nextMaintenance: maintenanceSchedule.nextMaintenanceDate,
        maintenanceSchedule: maintenanceSchedule,
        status: 'scheduled-maintenance'
      });
      
      console.log('Scheduled maintenance for equipment:', equipmentId);
    } catch (error) {
      console.error('Error scheduling maintenance:', error);
      throw error;
    }
  },

  /**
   * Get equipment maintenance history
   * @param {string} equipmentId - Equipment ID
   * @returns {Promise<Array>} Maintenance history
   */
  getEquipmentMaintenanceHistory: async (equipmentId) => {
    try {
      console.log('Getting equipment maintenance history via API:', equipmentId);
      
      const equipment = await surgicalEquipmentService.getEquipmentById(equipmentId);
      
      if (!equipment) {
        throw new Error('Equipment not found');
      }
      
      return equipment.maintenanceHistory || [];
    } catch (error) {
      console.error('Error getting equipment maintenance history:', error);
      throw error;
    }
  },

  /**
   * Export equipment data
   * @param {string} facilityId - Facility ID
   * @param {Object} filters - Export filters
   * @param {string} format - Export format ('json' or 'csv')
   * @returns {Promise<string>} Exported data
   */
  exportEquipment: async (facilityId, filters = {}, format = 'json') => {
    try {
      console.log('Exporting equipment via API:', facilityId, filters, format);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('format', format);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/surgical/surgical-equipment/facility/${facilityId}/export?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (format === 'json') {
        return JSON.stringify(response.data.equipment, null, 2);
      } else {
        return response.data.exportData;
      }
    } catch (error) {
      console.error('Error exporting equipment:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }
};

export default surgicalEquipmentService;
